"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "@/lib/hooks/use-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface LoginFormValues {
  email: string;
  password: string;
}

const GOOGLE_ENABLED = process.env.NEXT_PUBLIC_GOOGLE_AUTH_ENABLED === "true";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.5 0 10.4-1.9 14.3-5.1l-6.6-5.6C29.6 35 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.6 5.6C41.8 35.9 44 30.5 44 24c0-1.3-.1-2.7-.4-3.5z"/>
    </svg>
  );
}

export default function ConnexionPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const justRegistered = searchParams.get("inscrit") === "1";
  const verifState = searchParams.get("verif");

  const { values, update, error, setError, loading, submit } = useForm<LoginFormValues>({
    email: "",
    password: "",
  });

  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setNeedsVerification(false);
    await submit(async () => {
      const result = await signIn("credentials", {
        email: values.email,
        password: values.password,
        redirect: false,
      });

      if (result?.error === "email_not_verified") {
        setNeedsVerification(true);
        setError("Votre email n\'est pas encore verifie.");
        return;
      }

      if (result?.error) {
        setError("Email ou mot de passe incorrect");
        return;
      }

      router.push("/dashboard");
      router.refresh();
    });
  }

  async function handleResend() {
    setResendState("sending");
    try {
      await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: values.email }),
      });
      setResendState("sent");
    } catch {
      setResendState("idle");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6 py-12">
      <div className="w-full max-w-[400px] animate-fade-in-up rounded-2xl border-[1.5px] border-line bg-white p-8">
        <div className="mb-7 flex items-center justify-center gap-2.5">
          <svg width="28" height="28" viewBox="0 0 30 30">
            <rect width="30" height="30" rx="9" fill="#EB6D3B" />
            <path d="M15,20 C10,16.5 7,13.8 7,10.4 C7,7.9 9,6 11.4,6 C13,6 14.4,6.9 15,8.3 C15.6,6.9 17,6 18.6,6 C21,6 23,7.9 23,10.4 C23,13.8 20,16.5 15,20 Z" fill="#FFFFFF" />
          </svg>
          <span className="text-[19px] font-extrabold tracking-tight text-ink">mercilaba</span>
        </div>

        <h1 className="mb-1.5 text-center text-[24px] font-extrabold tracking-tight text-ink">Content de vous revoir</h1>
        <p className="mb-6 text-center text-[14px] text-muted">Connectez-vous pour gerer vos dons et votre page.</p>

        {justRegistered && (
          <div className="mb-5">
            <Alert variant="success">Compte cree ! Verifiez votre boite mail pour activer votre compte avant de vous connecter.</Alert>
          </div>
        )}

        {verifState === "ok" && (
          <div className="mb-5">
            <Alert variant="success">Email verifie avec succes, vous pouvez vous connecter.</Alert>
          </div>
        )}
        {verifState === "expire" && (
          <div className="mb-5">
            <Alert variant="error">Ce lien de verification a expire. Connectez-vous pour en recevoir un nouveau.</Alert>
          </div>
        )}
        {verifState === "erreur" && (
          <div className="mb-5">
            <Alert variant="error">Lien de verification invalide.</Alert>
          </div>
        )}

        {GOOGLE_ENABLED && (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setTimeout(() => signIn("google", { callbackUrl: "/dashboard" }), 150)}
              className="mb-4 flex items-center justify-center gap-2.5"
            >
              <GoogleIcon />
              Continuer avec Google
            </Button>
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-line" />
              <span className="text-[12px] font-semibold text-placeholder">OU</span>
              <div className="h-px flex-1 bg-line" />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-[13px] font-semibold text-ink">Email</label>
            <Input id="email" type="email" placeholder="vous@exemple.com" value={values.email} onChange={(e) => update("email", e.target.value)} required />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-[13px] font-semibold text-ink">Mot de passe</label>
            <Input id="password" type="password" placeholder="********" value={values.password} onChange={(e) => update("password", e.target.value)} required />
          </div>

          <Alert variant="error">{error}</Alert>

          {needsVerification && (
            <button
              type="button"
              onClick={handleResend}
              disabled={resendState !== "idle"}
              className="text-left text-[13px] font-bold text-ink underline decoration-line underline-offset-4 disabled:opacity-60"
            >
              {resendState === "sent" ? "Email renvoye !" : resendState === "sending" ? "Envoi..." : "Renvoyer l\'email de verification"}
            </button>
          )}

          <div className="pt-1">
            <Button type="submit" loading={loading} loadingText="Connexion...">Se connecter</Button>
          </div>
        </form>

        <p className="mt-6 text-center text-[13.5px] text-muted">
          Pas encore de page createur ? <a href="/inscription" className="font-bold text-ink underline decoration-line underline-offset-4 transition-opacity duration-150 hover:opacity-70">Creer un compte</a>
        </p>
      </div>
    </div>
  );
}
